/**
 * @file router.js
 * @description 客户端路由器 - SPA 路由管理
 * 
 * @features
 * - Hash-based 路由
 * - 动态路由注册
 * - 路由守卫（认证检查）
 * - 导航历史管理
 * - 页面生命周期管理
 * - 参数传递
 * 
 * @methods
 * - register(name, pageLoader): 注册路由
 * - navigate(name, params): 导航到指定路由
 * - back(): 返回上一页
 * - addGuard(guardFn): 添加路由守卫
 * 
 * @author AAA Team
 * @created 2025-09-01
 * @updated 2025-10-06
 */
import { appState } from './state.js'

class Router {
  constructor() {
    this.routes = {}
    this.currentPage = null
    this.guards = []
  }

  /**
   * 注册路由
   */
  register(name, pageLoader) {
    this.routes[name] = pageLoader
  }

  /**
   * 批量注册路由
   */
  registerRoutes(routes) {
    Object.entries(routes).forEach(([name, loader]) => {
      this.register(name, loader)
    })
  }

  /**
   * 添加路由守卫
   */
  addGuard(guard) {
    this.guards.push(guard)
  }

  /**
   * 导航到指定页面
   */
  async navigate(pageName, params = {}) {
    try {
      // 执行路由守卫
      for (const guard of this.guards) {
        const result = await guard(pageName, params)
        if (result === false) {
          console.log(`Navigation to ${pageName} blocked by guard`)
          return
        }
      }

      const pageLoader = this.routes[pageName]
      
      if (!pageLoader) {
        console.error(`Page ${pageName} not found`)
        this.navigate('404')
        return
      }

      // 显示加载状态
      appState.setLoading(true)

      // 更新导航UI
      this.updateNavigation(pageName)

      // ✨ 页面退出动画
      const app = document.getElementById('app')
      if (app && this.currentPage) {
        await this.pageExitAnimation(app)
      }

      // 加载页面
      const PageClass = await pageLoader()
      const page = new PageClass.default()
      
      // 卸载当前页面
      if (this.currentPage && typeof this.currentPage.unmount === 'function') {
        this.currentPage.unmount()
      }

      // 挂载新页面
      await page.mount(params)
      
      this.currentPage = page
      appState.setState({ currentPage: pageName })

      // ✨ 页面进入动画
      if (app) {
        await this.pageEnterAnimation(app)
      }

      // 更新浏览器历史
      history.pushState({ page: pageName, params }, '', `#${pageName}`)

      // 隐藏加载状态
      appState.setLoading(false)

      // 滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' })

    } catch (error) {
      console.error('Navigation failed:', error)
      appState.setLoading(false)
      appState.setError('Page load failed')
    }
  }

  /**
   * 页面退出动画
   */
  async pageExitAnimation(element) {
    return new Promise(resolve => {
      element.style.transition = 'opacity 0.15s ease-out, transform 0.15s ease-out'
      element.style.opacity = '0'
      element.style.transform = 'translateX(-20px)'
      setTimeout(resolve, 150)
    })
  }

  /**
   * 页面进入动画
   */
  async pageEnterAnimation(element) {
    return new Promise(resolve => {
      // 重置位置
      element.style.opacity = '0'
      element.style.transform = 'translateX(20px)'
      
      // 触发重绘
      element.offsetHeight
      
      // 进入动画
      element.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out'
      element.style.opacity = '1'
      element.style.transform = 'translateX(0)'
      
      setTimeout(() => {
        // 清理内联样式
        element.style.transition = ''
        resolve()
      }, 200)
    })
  }

  /**
   * 更新导航UI
   */
  updateNavigation(pageName) {
    document.querySelectorAll('.nav-btn').forEach(btn => {
      const btnPage = btn.getAttribute('data-page')
      if (btnPage === pageName) {
        btn.classList.add('active')
        btn.style.color = '#FFC83A'
      } else {
        btn.classList.remove('active')
        btn.style.color = 'rgba(255,255,255,0.6)'
      }
    })
  }

  /**
   * 初始化路由
   */
  init() {
    // 监听浏览器前进后退
    window.addEventListener('popstate', (e) => {
      if (e.state?.page) {
        this.navigate(e.state.page, e.state.params)
      }
    })

    // 监听hash变化
    window.addEventListener('hashchange', () => {
      const hash = window.location.hash.slice(1)
      if (hash && this.routes[hash]) {
        this.navigate(hash)
      }
    })

    // 初始页面
    const hash = window.location.hash.slice(1) || 'home'
    this.navigate(hash)
  }

  /**
   * 返回上一页
   */
  back() {
    history.back()
  }

  /**
   * 前进
   */
  forward() {
    history.forward()
  }
}

// 导出单例
export const router = new Router()

// 注意：认证守卫在 main.js 中配置
